import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import RemoveIcon from "@mui/icons-material/Remove";
import { format } from "date-fns";

import { useIdleTimer } from "react-idle-timer";
import { useAppSelector } from "hooks/reduxHooks";
import { Formatter } from "modules/utils";
import { MIN_SECONDS_VALUE, MINUTES_THRESHOLD } from "modules/auth/models";

// A countdown as m:ss, floored at zero. Reuses the shared formatter so it reads like the rest
// of the app (the session-timeout dialog uses the same helper).
const countdown = (ms: number): string => Formatter.formatSecondsToMinutes(Math.max(0, Math.ceil(ms / 1000)));

interface DiagnosticRowProps {
    label: string;
    value: React.ReactNode;
    hint?: React.ReactNode;
}

const DiagnosticRow = ({ label, value, hint }: DiagnosticRowProps) => (
    <div className="diagnostics-panel-row">
        <span className="label">{label}</span>
        <span className="value">{value}</span>
        {hint && <span className="hint">{hint}</span>}
    </div>
);

// Developer/QA-only panel showing live session and token timing. It is observation-only: it
// reads existing redux session state and runs a passive idle timer, and never extends, ends,
// or otherwise mutates the session. Gated by config.showDiagnosticsPanel in LayoutBase so it
// only mounts in non-production environments.
export const DiagnosticsPanel = () => {
    // Starts as the bubble so the tool never covers the app on load. This lives in component
    // state (not storage) on purpose: LayoutBase stays mounted while navigating between pages,
    // so an expand sticks as you click around, but a full page reload/re-login starts minimized
    // again.
    const [minimized, setMinimized] = useState(true);

    // A once-per-second re-render so every countdown ticks. Derived values are computed in
    // render against Date.now(), so we only need to nudge React, not store the values.
    const [, setTick] = useState(0);

    const session = useAppSelector((state) => state.auth.session);
    const accessTokenExpiry = session?.accessTokenExpiry ?? 0;
    const reauthRequiredMs = session?.inactiveReauthRequiredMs ?? 0;
    const idle = session?.idle ?? false;

    // A first-stage MFA session has a userId but hasn't completed OTP, so it is not yet
    // authenticated (matches SessionTimer). The panel only makes sense for a real session, and
    // this also stops it flashing on the brief pre-redirect render before /login takes over.
    const isAuthenticated = !!session?.userId && !session?.isFirstStage2fa;

    // A passive mirror of the real inactivity timer (modules/auth/components/sessionTimer.tsx).
    // Same timeout and default activity events, but no onIdle/onAction handlers, so reading its
    // remaining time cannot trigger a logout or a session extension.
    const idleTimer = useIdleTimer({
        timeout: reauthRequiredMs || 1,
        startOnMount: true
    });

    useEffect(() => {
        // Only tick while the expanded panel is actually on screen; the bubble and the
        // unauthenticated (null) render have nothing to update.
        if (!isAuthenticated || minimized) {
            return undefined;
        }

        const interval = setInterval(() => setTick((value) => value + 1), 1000);
        return () => clearInterval(interval);
    }, [isAuthenticated, minimized]);

    const now = Date.now();

    // Inactivity: time until the reauth prompt fires. Once idle, the session-timeout dialog owns
    // the countdown from there, so the panel just reports the locked state.
    const reauthRemainingMs = idle ? 0 : idleTimer.getRemainingTime();

    // Access token expiry (normalized to a millisecond epoch).
    const hasAccessToken = accessTokenExpiry > 0;
    const accessTokenExpiryMs = accessTokenExpiry < MIN_SECONDS_VALUE ? accessTokenExpiry * 1000 : accessTokenExpiry;
    const accessTokenRemainingMs = accessTokenExpiryMs - now;
    const accessTokenExpired = hasAccessToken && accessTokenRemainingMs <= 0;
    const accessTokenRefreshImminent =
        hasAccessToken && !accessTokenExpired && accessTokenRemainingMs <= MINUTES_THRESHOLD * 60 * 1000;

    if (!isAuthenticated) {
        return null;
    }

    if (minimized) {
        return (
            <Tooltip title="Developer diagnostics" placement="left">
                <button
                    type="button"
                    aria-label="Open developer diagnostics panel"
                    className="diagnostics-bubble"
                    onClick={() => setMinimized(false)}
                >
                    <BugReportIcon />
                </button>
            </Tooltip>
        );
    }

    return (
        <div className="diagnostics-panel">
            <div className="diagnostics-panel-header">
                <BugReportIcon fontSize="small" className="icon" />
                <span className="title">DIAGNOSTICS</span>
                <Tooltip title="Minimize">
                    <button
                        type="button"
                        aria-label="Minimize developer diagnostics panel"
                        className="minimize"
                        onClick={() => setMinimized(true)}
                    >
                        <RemoveIcon fontSize="small" />
                    </button>
                </Tooltip>
            </div>

            <DiagnosticRow
                label="Session timeout dialog in"
                value={idle ? "Dialog showing" : countdown(reauthRemainingMs)}
            />
            <DiagnosticRow
                label="Access token expires"
                value={
                    hasAccessToken
                        ? accessTokenExpired
                            ? "Expired"
                            : `${countdown(accessTokenRemainingMs)} (${format(accessTokenExpiryMs, "pp")})`
                        : "N/A"
                }
                hint={accessTokenRefreshImminent ? "Refreshes on next request" : undefined}
            />
        </div>
    );
};

export default DiagnosticsPanel;
